import type { BookFormat, BookProcessingStatus } from '@masters-club/types';

export interface BookTranslationData {
  id?: number;
  languageCode: string;
  title: string;
  description: string | null;
  authorDisplay: string | null;
}

export interface BookChapterTranslationData {
  id?: number;
  languageCode: string;
  title: string;
}

export interface BookChapterData {
  id?: number;
  order: number;
  audioFileId: number | null;
  audioFileUrl: string | null;
  audioFileOriginalName: string | null;
  hlsPlaylistUrl: string | null;
  durationSec: number | null;
  pageStart: number | null;
  pageEnd: number | null;
  translations: BookChapterTranslationData[];
}

/**
 * Полный объект книги из API (для list/show/edit).
 */
export interface Book {
  id: number;
  slug: string;
  format: BookFormat;
  coverFileId: number | null;
  coverFileUrl: string | null;
  sourceFileId: number | null;
  sourceFileUrl: string | null;
  sourceFileOriginalName: string | null;
  renderFileId: number | null;
  author: string | null;
  languageCode: string;
  translationGroupId: string | null;
  totalPages: number | null;
  totalDurationSec: number | null;
  pricingTierId: number | null;
  isPublished: boolean;
  publishedAt: string | null;
  processingStatus: BookProcessingStatus;
  processingError: string | null;
  createdAt: string;
  updatedAt: string;
  translations: BookTranslationData[];
  chapters: BookChapterData[];
}

/**
 * Payload для создания книги.
 */
export interface BookFormData {
  slug: string;
  format: BookFormat;
  coverFileId: number | null;
  sourceFileId: number | null;
  author: string | null;
  languageCode: string;
  translationGroupId: string | null;
  pricingTierId: number | null;
  totalPages: number | null;
  totalDurationSec: number | null;
  translations: BookTranslationData[];
}

export interface BookChapterFormData {
  order: number;
  audioFileId: number | null;
  durationSec: number | null;
  pageStart: number | null;
  pageEnd: number | null;
  translations: BookChapterTranslationData[];
}

export const BOOK_FORMAT_OPTIONS: { value: BookFormat; label: string; description: string }[] = [
  { value: 'EPUB', label: 'EPUB', description: 'Стандарт электронных книг' },
  { value: 'PDF', label: 'PDF', description: 'Постраничный PDF' },
  { value: 'FB2', label: 'FB2', description: 'FictionBook (конвертируется в EPUB)' },
  { value: 'AUDIO', label: 'Аудиокнига', description: 'Главы как аудиофайлы / HLS' },
];

export function getBookTitle(book: Pick<Book, 'slug' | 'translations'>, lang = 'ru'): string {
  const translations = book.translations ?? [];
  return (
    translations.find((t) => t.languageCode === lang)?.title
    ?? translations[0]?.title
    ?? book.slug
  );
}

export function getChapterTitle(chapter: Pick<BookChapterData, 'order' | 'translations'>, lang = 'ru'): string {
  const translations = chapter.translations ?? [];
  return (
    translations.find((t) => t.languageCode === lang)?.title
    ?? translations[0]?.title
    ?? `Глава ${chapter.order + 1}`
  );
}
